import { useId, useState, type ReactNode } from 'react';
import { DialogLayer } from '../ui/Dialog';
import { selfMarkAsk, selfMarkQuestion, type SelfMarkDetail } from './peopleGrid';

/**
 * Owner 2026-10-29 00:29 („musisz mnie ostrzec i zapytać czy decyzja o zmianie została skonsultowana z pracownikiem”):
 * before a change from the grid overwrites marks the people set themselves in „Mój kalendarz” — one cell or every chosen
 * cell in one list. „Tak, zmień” sends the change again with `consulted`; „Anuluj” (or Esc) changes nothing.
 * Owner 2026-10-30 01:28: marks from the person's answer to a question too — the words say where each comes from.
 */
export function SelfMarkDialog({ marks, nameOf, busy, onConfirm, onClose }: { marks: readonly SelfMarkDetail[]; nameOf: (personId: string) => string; busy: boolean; onConfirm: () => void; onClose: () => void }) {
  const id = useId();
  const words = selfMarkQuestion(marks, nameOf);
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby={id} data-feature-id="crew.people_availability">
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id={id}>Wpis pracownika</h2>
        <p>{words.lead}</p>
        {words.lines.length > 0 && (
          <ul className="pg-question-list">
            {words.lines.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        )}
        <p>
          <strong>{words.ask}</strong>
        </p>
        <p className="muted">W historii dnia zostanie zapisane, że zmianę skonsultowano z pracownikiem.</p>
        <div className="actions">
          <button className="btn primary" type="button" disabled={busy} autoFocus onClick={onConfirm}>
            {busy ? 'Zapisywanie…' : 'Tak, zmień'}
          </button>
          <button className="btn" type="button" disabled={busy} onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Review I2: the same question where a status „Odmowa” may overwrite the person's own mark (Obsada's status select, the
 * side panel's „Zmień status…”, „Dodaj osobę” as „Odmowa”). `offer(error, retry)`: a 422 `self_mark` opens the dialog
 * (true — not an error to show); „Tak, zmień” calls `retry` with the token to send as `consulted`.
 */
export function useSelfMarkAsk(nameOf: (personId: string) => string = () => 'Osoba'): { offer: (error: unknown, retry: (token: string) => void) => boolean; dialog: ReactNode } {
  const [ask, setAsk] = useState<{ marks: SelfMarkDetail[]; retry: () => void } | null>(null);
  const offer = (error: unknown, retry: (token: string) => void) => {
    const found = selfMarkAsk(error);
    if (found) setAsk({ marks: found.marks, retry: () => retry(found.token) });
    return !!found;
  };
  const dialog = ask ? (
    <SelfMarkDialog
      marks={ask.marks}
      nameOf={nameOf}
      busy={false}
      onConfirm={() => {
        const { retry } = ask;
        setAsk(null);
        retry();
      }}
      onClose={() => setAsk(null)}
    />
  ) : null;
  return { offer, dialog };
}
